import {
  bankOperationFailed as companiesBankOperationFailed,
  insuranceOperationFailed as companiesInsuranceOperationFailed,
  leasingOperationFailed as companiesLeasingOperationFailed,
} from "Store/companies/slice";
import {
  operationFailed as priceCalculationOperationFailed,
  storedProductOperationFailed,
} from "Store/priceCalculation/priceCalculationSlice";
import { ErrorContext, Log } from "Utils/sentryLogUtils";

import { operationFailed as addProductToCartOperationFailed } from "Store/addProductToCart/slice";
import { operationFailed as adviceOperationFailed } from "Store/advice/slice";
import { operationFailed as advicePDFOperationFailed } from "Store/advicePDF/slice";
import { ConfirmationStatus } from "Store/cart/cartInterfaces";
import { operationFailed as cartOperationFailed } from "Store/cart/cartSlice";
import { operationFailed as downtimeOperationFailed } from "Store/downtime/slice";
import { OperationType as FindVehicleOperationType } from "Store/findVehicle/interfaces";
import { operationFailed as findVehicleOperationFailed } from "Store/findVehicle/slice";
import { operationFailed as findVehicleTypeOperationFailed } from "Store/findVehicleType/slice";
import { operationFailed as offerEmailOperationFailed } from "Store/offerEmail/slice";
import { fetchCityFailed as postNumberOperationFailed } from "Store/postNumber/postNumberSlice";
import { textsFailed } from "Store/texts/slice";
import { operationFailed as userIdentificationOperationFailed } from "Store/userIdentification/userIdentificationSlice";
import { validateLicensePlateError } from "Store/validateLicensePlate/slice";
import { isDefined } from "Utils/functions";
import { takeEvery } from "redux-saga/effects";

type AllOperationFailedTypes =
  | typeof addProductToCartOperationFailed
  | typeof adviceOperationFailed
  | typeof advicePDFOperationFailed
  | typeof cartOperationFailed
  | typeof companiesBankOperationFailed
  | typeof companiesInsuranceOperationFailed
  | typeof companiesLeasingOperationFailed
  | typeof downtimeOperationFailed
  | typeof findVehicleOperationFailed
  | typeof findVehicleTypeOperationFailed
  | typeof offerEmailOperationFailed
  | typeof postNumberOperationFailed
  | typeof priceCalculationOperationFailed
  | typeof storedProductOperationFailed
  | typeof textsFailed
  | typeof userIdentificationOperationFailed
  | typeof validateLicensePlateError;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* operationFailedSentry(
  sagaAction: ReturnType<AllOperationFailedTypes>,
) {
  const name = getErrorName(sagaAction);
  const errorContext = getErrorContext(sagaAction);
  if (
    sagaAction.type !== "cart/operationFailed" ||
    sagaAction.payload.response?.detail !== ConfirmationStatus.CART_IS_CONFIRMED
  ) {
    yield Log.error(name, errorContext);
  }
}

const getErrorName = (
  sagaAction: ReturnType<AllOperationFailedTypes>,
): string => {
  const { type: actionType, payload: actionPayload } = sagaAction;

  const operationType =
    typeof actionPayload !== "string" && "operationType" in actionPayload
      ? actionPayload.operationType
      : undefined;

  return isDefined(operationType)
    ? `${actionType} - ${operationType}`
    : actionType;
};

const getErrorContext = (
  sagaAction: ReturnType<AllOperationFailedTypes>,
): ErrorContext => {
  const { type: actionType, payload: actionPayload } = sagaAction;

  if (typeof actionPayload !== "string") {
    if ("operationType" in actionPayload) {
      // FindVehicleError
      if (
        actionPayload.operationType === FindVehicleOperationType.FIND_VEHICLE
      ) {
        return {
          tags: { actionType, operationType: actionPayload.operationType },
          extras: { response: actionPayload.response },
        };
      }
      return {
        tags: { actionType, operationType: actionPayload.operationType },
      };
    }
    if ("error" in actionPayload) {
      if ("errorType" in actionPayload.error) {
        // LicensePlateValidationError
        return {
          tags: { actionType, operationType: actionPayload.error.errorType },
          extras: actionPayload,
        };
      }
      if ("operationType" in actionPayload.error) {
        // FindVehicleTypeError
        return {
          tags: {
            actionType,
            operationType: actionPayload.error.operationType,
          },
          extras: actionPayload,
        };
      }
    }
  }

  return {
    tags: { actionType },
    extras:
      typeof actionPayload === "string"
        ? { message: actionPayload }
        : actionPayload,
  };
};

export function* operationFailedSaga(): Generator {
  yield takeEvery(
    [
      addProductToCartOperationFailed,
      adviceOperationFailed,
      advicePDFOperationFailed,
      cartOperationFailed,
      companiesBankOperationFailed,
      companiesInsuranceOperationFailed,
      companiesLeasingOperationFailed,
      downtimeOperationFailed,
      findVehicleOperationFailed,
      findVehicleTypeOperationFailed,
      offerEmailOperationFailed,
      postNumberOperationFailed,
      priceCalculationOperationFailed,
      storedProductOperationFailed,
      textsFailed,
      userIdentificationOperationFailed,
      validateLicensePlateError,
    ],
    operationFailedSentry,
  );
}
